import { QueryClient } from 'react-query';
import { SideWithAsset } from 'shared-types';

import useLayoutStore from 'hooks/stores/useLayoutStore';
import useProductsStore from 'hooks/stores/useProductsStore';

import API from 'services/API';

// Hoe lang we de backend pollen op de nieuw aangemaakte blanco side voordat we opgeven
// (het aanmaken van het asset loopt via een queue).
const BLANK_PAGE_POLL_TIMEOUT_MS = 20000;
const BLANK_PAGE_POLL_INTERVAL_MS = 1000;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const getLatestSides = (productId: string): SideWithAsset[] =>
  (useProductsStore.getState().products.find((p) => p.id === productId)?.sides ??
    []) as SideWithAsset[];

const pollForNewSide = async (
  queryClient: QueryClient,
  productId: string,
  sideIdsBefore: Set<string>,
): Promise<SideWithAsset> => {
  const start = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await queryClient.invalidateQueries('products');
    const newSide = getLatestSides(productId).find((side) => !sideIdsBefore.has(side.id));
    if (newSide) return newSide;
    if (Date.now() - start > BLANK_PAGE_POLL_TIMEOUT_MS) {
      throw new Error('Timed out waiting for the blank page to be created');
    }
    await sleep(BLANK_PAGE_POLL_INTERVAL_MS);
  }
};

export type InsertBlankPageParams = {
  sessionId: string;
  productId: string;
  // 0-based positie waar de blanco pagina moet komen; de pagina die daar stond en alles erna
  // schuift één plek naar rechts.
  targetIndex: number;
  queryClient: QueryClient;
};

export type InsertBlankPageResult = { sideId: string; index: number };

// Gedeeld door BookPreviewModal en de booklayout-grid. Maakt een blanco pagina aan (die achteraan
// wordt toegevoegd) en "bubbelt" hem daarna naar de gewenste plek door hem steeds met zijn linker
// buur te wisselen — de backend kent alleen een paarsgewijze orderNumber-swap, geen insert-at-index.
export const insertBlankPageAt = async ({
  sessionId,
  productId,
  targetIndex,
  queryClient,
}: InsertBlankPageParams): Promise<InsertBlankPageResult> => {
  const sideIdsBefore = new Set(getLatestSides(productId).map((side) => side.id));

  const response = await API.postCreateBlankPage(sessionId, productId);
  if (response.status !== 202) throw new Error('Failed to create blank page');
  useLayoutStore.getState().setShouldDisplayAssetsProcessing(true);

  const newSide = await pollForNewSide(queryClient, productId, sideIdsBefore);

  // getLatestSides() leest rechtstreeks uit de immer-store, die zijn arrays in development
  // freezet — eerst kopiëren voordat we hieronder in place muteren.
  const working = [...getLatestSides(productId)];
  let pos = working.findIndex((side) => side.id === newSide.id);
  const clampedTarget = Math.min(Math.max(targetIndex, 0), working.length - 1);
  while (pos > clampedTarget) {
    const neighbor = working[pos - 1];
    // eslint-disable-next-line no-await-in-loop
    await API.swapPageNumbers({ sessionId, sideId: newSide.id, targetSideId: neighbor.id });
    [working[pos - 1], working[pos]] = [working[pos], working[pos - 1]];
    pos -= 1;
  }

  await queryClient.invalidateQueries('products');

  return { sideId: newSide.id, index: clampedTarget };
};
